// Copyright (c) Ping. All rights reserved.
//
// Cross-platform Ping logomark renderer (web + default fallback).
//
// react-native-svg has no runtime on react-native-web (the project
// strips it via vite alias — see apps/web/vite.config.ts), and
// passing `dangerouslySetInnerHTML` through `<View>` is filtered out
// by rn-web's prop allowlist, so the earlier approach didn't render.
//
// We encode the SVG as a `data:image/svg+xml;base64,…` URI and feed
// it to RN's <Image>. On web, rn-web renders an `<img src=…>` and the
// browser natively parses the SVG (including filters + gradients);
// the result is pixel-identical to the Figma export. On macOS the
// `.macos.tsx` sibling renders the same XML through SvgXml.

import { Image } from 'react-native';
import { PING_LOGOMARK_SVG } from './pingLogomarkSvg';

export interface PingLogomarkProps {
	readonly size?: number;
	readonly accessibilityLabel?: string;
}

// btoa is available in browsers and Node 16+. The SVG is pure ASCII,
// so we don't need the unicode workaround.
const SVG_BASE64: string =
	typeof btoa !== 'undefined'
		? btoa(PING_LOGOMARK_SVG)
		// Fallback for non-browser non-Node environments (unlikely; just
		// keep the component renderable so the tree doesn't crash).
		: '';
const SVG_DATA_URI = `data:image/svg+xml;base64,${SVG_BASE64}`;

export function PingLogomark({ size = 80, accessibilityLabel }: PingLogomarkProps): JSX.Element {
	return (
		<Image
			source={{ uri: SVG_DATA_URI }}
			accessibilityLabel={accessibilityLabel ?? 'Ping'}
			accessibilityIgnoresInvertColors
			style={{ width: size, height: size }}
			resizeMode="contain"
		/>
	);
}
